import SiteNoticeLayout, {
  NoticePiece,
} from "../../../components/SiteNotice/Component";
import { announcementSettings } from "../../../Core/Content/announcement.mjs";
import FeaturedMenu from "./FeaturedMenu";
import { publicContentFn } from "../../../Core/Content/Functions";
import PreviewComponent from "../../../Shared/UI/Preview/Component";
import React from "react";
import { PageId, PageContent } from "../../../Core/Content/Types";
import { Session } from "../../../Core/Auth/adapter";
import { usePageEditor } from "./Hooks";
import {
  BasicFieldsPiece,
  ItemFieldsPiece,
  MediaFieldPiece,
  TextFieldPiece,
} from "./Pieces";
import { moveItemFn, newItemFn } from "./Functions";
import { PreviewContext } from "../../../Core/Content/Preview";
import HomeComponent from "../../../pages/Home/Component";
import AboutComponent from "../../../pages/About/Component";
import FooterComponent from "../../../components/Footer";
import NavComponent from "../../../components/Nav";
import MenuComponent from "../../../pages/Menu/Component";
export default function PageEditorComponent({
  pageId,
  session,
  selectedItemId,
}: {
  pageId: PageId;
  session: Session;
  selectedItemId?: string;
}) {
  const editor = usePageEditor(pageId);
  const [preview, setPreview] = React.useState(false);
  const [narrow, setNarrow] = React.useState(false);
  const content = editor.content;
  if (!content)
    return (
      <div role="status">
        {editor.busy
          ? "Loading saved content…"
          : editor.message || "Content unavailable."}
        <p>
          <a href="/dashboard">Back to dashboard</a>
        </p>
      </div>
    );
  const set = (value: PageContent) => editor.setContent(value);
  const uploadField = (file: File, key: keyof PageContent) =>
    editor.upload(file, (url) =>
      editor.setContent((current) =>
        current ? { ...current, [key]: url } : current
      )
    );
  const fields: [keyof PageContent, string][] =
    pageId === "navigation"
      ? []
      : pageId === "footer"
      ? [
          ["fullName", "Business name"],
          ["addressTop", "Street address"],
          ["addressBottom", "City, state and ZIP"],
          ["email", "Email"],
          ["phone", "Phone"],
          ["hours", "Opening hours"],
          ["registry", "Footer text"],
          ["instagram", "Instagram link"],
          ["facebook", "Facebook link"],
          ["tiktok", "TikTok link"],
          ["sowLogo", "Footer logo URL"],
          ["logoAlt", "Logo image description"],
          ["bkgImg", "Footer background URL"],
        ]
      : pageId === "home"
      ? [
          ["bannerVideoUrl", "Banner video URL"],
          ["orderNowUrl", "Order now URL"],
          ["sowLogo", "Logo URL"],
          ["logoAlt", "Logo image description"],
          ["featuredHeading", "Featured food heading"],
          ["featuredDescription", "Featured food introduction"],
          ["drinksHeading", "Featured drinks heading"],
          ["drinksDescription", "Featured drinks introduction"],
        ]
      : pageId === "about"
      ? [["meetTheOwners", "Owners video URL"]]
      : [
          ["title", "Menu title"],
          ["subtitle", "Menu introduction"],
        ];
  const draftPreview = publicContentFn(pageId, content, editor.menu);
  return (
    <>
      <a
        href="/dashboard"
        onClick={(event) => {
          if (
            editor.dirty &&
            !window.confirm("Leave this page and discard unsaved changes?")
          )
            event.preventDefault();
        }}
      >
        ← Dashboard
      </a>
      <header className="cms-toolbar">
        <div>
          <h1>
            {pageId === "menu" ? "Menu & seasonal items" : `Edit ${pageId}`}
          </h1>
          <p>
            {editor.dirty ? "Unsaved changes" : "Saved draft"}
            {editor.saved &&
              ` · ${new Date(editor.saved.draft.updatedAt).toLocaleString()}`}
          </p>
        </div>
        <div className="cms-actions">
          <button
            disabled={editor.busy || editor.uploading || !editor.dirty}
            onClick={editor.reset}
          >
            Reset
          </button>
          <button
            disabled={
              !session.canEdit ||
              editor.busy ||
              editor.uploading ||
              !editor.dirty ||
              !!editor.errors.length
            }
            onClick={editor.save}
          >
            Save draft
          </button>
          <button
            className="cms-primary"
            disabled={
              !session.canPublish ||
              editor.busy ||
              editor.uploading ||
              editor.dirty ||
              !!editor.errors.length
            }
            onClick={editor.publishSaved}
          >
            Publish saved revision
          </button>
        </div>
      </header>
      <p className="cms-note">
        Save a draft first, then publish when it is ready. Seasonal items can be
        hidden and restored at any time.
      </p>
      {editor.saved && (
        <small>Draft revision: {editor.saved.draft.revision}</small>
      )}
      {editor.message && (
        <p
          className={editor.failed ? "cms-error" : "cms-success"}
          role={editor.failed ? "alert" : "status"}
        >
          {editor.message}
        </p>
      )}
      {!!editor.errors.length && (
        <div className="cms-error" role="alert">
          <strong>Check these fields before saving:</strong>
          <ul>
            {editor.errors.map((message, index) => (
              <li key={index}>{message}</li>
            ))}
          </ul>
        </div>
      )}
      <fieldset
        disabled={!session.canEdit || editor.uploading}
        className="cms-fields"
      >
        {pageId === "navigation" && (
          <section className="cms-card">
            <h2>Scrolling ordering notice</h2>
            <p>
              This gold banner appears at the top of Home, About, and Menu. Save
              and publish Navigation to update it for visitors.
            </p>
            <label className="cms-toggle">
              <input
                type="checkbox"
                checked={announcementSettings(content).enabled}
                onChange={(event) =>
                  set({
                    ...content,
                    announcement: {
                      ...announcementSettings(content),
                      enabled: event.target.checked,
                    },
                  })
                }
              />
              Show the ordering notice
            </label>
            <TextFieldPiece
              label="Banner message"
              multiline
              value={announcementSettings(content).message}
              onChange={(message) =>
                set({
                  ...content,
                  announcement: { ...announcementSettings(content), message },
                })
              }
            />
            <small>
              {announcementSettings(content).message.length}/300 characters.
              This is a notice only; it does not disable online ordering.
            </small>
            <h3>Banner preview</h3>
            {announcementSettings(content).enabled ? (
              <NoticePiece settings={announcementSettings(content)} />
            ) : (
              <p>The banner is turned off.</p>
            )}
          </section>
        )}
        <BasicFieldsPiece
          content={content}
          fields={fields}
          onChange={set}
          uploading={editor.uploading}
          onUpload={uploadField}
        />
        {pageId !== "footer" && pageId !== "navigation" && (
          <BasicFieldsPiece
            uploading={editor.uploading}
            onUpload={uploadField}
            content={content}
            fields={[
              ["metaTitle", "Browser title"],
              ["metaDescription", "Search description"],
              ["socialImage", "Social sharing image URL"],
            ]}
            onChange={set}
          />
        )}
        {(pageId === "home" || pageId === "about") &&
          (pageId === "about"
            ? (["checkUsOut", "whatOurGuestsSay"] as const)
            : (["checkUsOut"] as const)
          ).map((key) => (
            <section className="cms-card" key={key}>
              <h2>{key === "checkUsOut" ? "Our story" : "Guest section"}</h2>
              <label className="cms-toggle">
                <input
                  type="checkbox"
                  checked={content[key].visible}
                  onChange={(event) =>
                    set({
                      ...content,
                      [key]: { ...content[key], visible: event.target.checked },
                    })
                  }
                />
                Show this section
              </label>
              {(
                [
                  "title",
                  "content",
                  "firstImg",
                  "secondImg",
                  "thirdImg",
                  "firstAlt",
                  "secondAlt",
                  "thirdAlt",
                ] as const
              ).map((field) =>
                field.endsWith("Img") ? (
                  <MediaFieldPiece
                    key={field}
                    label={
                      (
                        {
                          firstImg: "First photo",
                          secondImg: "Second photo",
                          thirdImg: "Third photo",
                        } as Record<string, string>
                      )[field] || "Photo"
                    }
                    value={content[key][field]}
                    uploading={editor.uploading}
                    onUpload={(file) =>
                      editor.upload(file, (url) =>
                        editor.setContent((current) =>
                          current
                            ? {
                                ...current,
                                [key]: { ...current[key], [field]: url },
                              }
                            : current
                        )
                      )
                    }
                    onRemove={() =>
                      set({
                        ...content,
                        [key]: { ...content[key], [field]: "" },
                      })
                    }
                  />
                ) : (
                  <TextFieldPiece
                    key={field}
                    label={
                      {
                        title: "Section heading",
                        content: "Section text",
                        firstAlt: "First photo description",
                        secondAlt: "Second photo description",
                        thirdAlt: "Third photo description",
                        firstImg: "First photo",
                        secondImg: "Second photo",
                        thirdImg: "Third photo",
                      }[field]
                    }
                    multiline={field === "content"}
                    value={content[key][field]}
                    onChange={(value) =>
                      set({
                        ...content,
                        [key]: { ...content[key], [field]: value },
                      })
                    }
                  />
                )
              )}
            </section>
          ))}
        {pageId === "menu" &&
          content.categories.map((category, categoryIndex) => (
            <details
              className="cms-card"
              key={category.id}
              open={
                categoryIndex === 0 ||
                category.items.some((item) => item.id === selectedItemId)
              }
            >
              <summary>
                {category.title} · {category.items.length} items
              </summary>
              <label className="cms-toggle">
                <input
                  type="checkbox"
                  checked={category.visible}
                  onChange={(event) =>
                    set({
                      ...content,
                      categories: content.categories.map((c) =>
                        c.id === category.id
                          ? { ...c, visible: event.target.checked }
                          : c
                      ),
                    })
                  }
                />
                Show category
              </label>
              <div className="cms-actions">
                {[-1, 1].map((direction) => (
                  <button
                    key={direction}
                    disabled={
                      direction === -1
                        ? categoryIndex === 0
                        : categoryIndex === content.categories.length - 1
                    }
                    onClick={() =>
                      set({
                        ...content,
                        categories: moveItemFn(
                          content.categories,
                          categoryIndex,
                          direction
                        ),
                      })
                    }
                  >
                    {direction === -1 ? "Category up" : "Category down"}
                  </button>
                ))}
              </div>
              <TextFieldPiece
                label="Category title"
                value={category.title}
                onChange={(title) =>
                  set({
                    ...content,
                    categories: content.categories.map((c) =>
                      c.id === category.id ? { ...c, title } : c
                    ),
                  })
                }
              />
              {category.items.map((item, index) => (
                <details
                  key={item.id}
                  className="cms-item"
                  open={selectedItemId === item.id ? true : undefined}
                >
                  <summary>
                    {item.title || "New item"}{" "}
                    <span>
                      {item.price || "No price"} ·{" "}
                      {item.visible ? "Visible" : "Hidden / seasonal"}
                    </span>
                  </summary>
                  <a
                    href={`/dashboard/menu/${encodeURIComponent(item.id)}`}
                    onClick={(event) => {
                      if (
                        editor.dirty &&
                        !window.confirm(
                          "Discard unsaved changes and open the direct item editor?"
                        )
                      )
                        event.preventDefault();
                    }}
                  >
                    Direct link to this item
                  </a>
                  <ItemFieldsPiece
                    item={item}
                    uploading={editor.uploading}
                    onUpload={(file, sideIndex) =>
                      editor.upload(file, (url) =>
                        editor.setContent((current) =>
                          current
                            ? {
                                ...current,
                                categories: current.categories.map((c) =>
                                  c.id === category.id
                                    ? {
                                        ...c,
                                        items: c.items.map((i) =>
                                          i.id === item.id
                                            ? sideIndex === undefined
                                              ? { ...i, img: url }
                                              : {
                                                  ...i,
                                                  includes: i.includes.map(
                                                    (part, index) =>
                                                      index === sideIndex
                                                        ? { ...part, img: url }
                                                        : part
                                                  ),
                                                }
                                            : i
                                        ),
                                      }
                                    : c
                                ),
                              }
                            : current
                        )
                      )
                    }
                    onChange={(value) =>
                      set({
                        ...content,
                        categories: content.categories.map((c) =>
                          c.id === category.id
                            ? {
                                ...c,
                                items: c.items.map((i) =>
                                  i.id === item.id ? value : i
                                ),
                              }
                            : c
                        ),
                      })
                    }
                  />
                  <div className="cms-actions">
                    {[-1, 1].map((direction) => (
                      <button
                        key={direction}
                        disabled={
                          direction === -1
                            ? index === 0
                            : index === category.items.length - 1
                        }
                        onClick={() =>
                          set({
                            ...content,
                            categories: content.categories.map((c) =>
                              c.id === category.id
                                ? {
                                    ...c,
                                    items: moveItemFn(
                                      c.items,
                                      index,
                                      direction
                                    ),
                                  }
                                : c
                            ),
                          })
                        }
                      >
                        {direction === -1 ? "Move up" : "Move down"}
                      </button>
                    ))}
                    <button
                      onClick={() => {
                        if (
                          window.confirm(
                            `Remove ${
                              item.title || "this item"
                            } from the draft? Hide it instead if it will return seasonally.`
                          )
                        )
                          set({
                            ...content,
                            categories: content.categories.map((c) =>
                              c.id === category.id
                                ? {
                                    ...c,
                                    items: c.items.filter(
                                      (i) => i.id !== item.id
                                    ),
                                  }
                                : c
                            ),
                          });
                      }}
                    >
                      Remove item
                    </button>
                  </div>
                </details>
              ))}
              <button
                onClick={() =>
                  set({
                    ...content,
                    categories: content.categories.map((c, i) =>
                      i === categoryIndex
                        ? {
                            ...c,
                            items: [...c.items, newItemFn(crypto.randomUUID())],
                          }
                        : c
                    ),
                  })
                }
              >
                + Add item
              </button>
            </details>
          ))}
        {pageId === "navigation" && (
          <section className="cms-card">
            <h2>Navigation links</h2>
            {content.navigation.map((link, index) => (
              <div className="cms-item" key={link.id}>
                <TextFieldPiece
                  label="Link label"
                  value={link.title}
                  onChange={(title) =>
                    set({
                      ...content,
                      navigation: content.navigation.map((l) =>
                        l.id === link.id ? { ...l, title } : l
                      ),
                    })
                  }
                />
                <TextFieldPiece
                  label="Destination"
                  value={link.href}
                  onChange={(href) =>
                    set({
                      ...content,
                      navigation: content.navigation.map((l) =>
                        l.id === link.id ? { ...l, href } : l
                      ),
                    })
                  }
                />
                <label className="cms-toggle">
                  <input
                    type="checkbox"
                    checked={link.visible}
                    onChange={(e) =>
                      set({
                        ...content,
                        navigation: content.navigation.map((l) =>
                          l.id === link.id
                            ? { ...l, visible: e.target.checked }
                            : l
                        ),
                      })
                    }
                  />
                  Show link
                </label>
                <div className="cms-actions">
                  {[-1, 1].map((direction) => (
                    <button
                      key={direction}
                      disabled={
                        direction === -1
                          ? index === 0
                          : index === content.navigation.length - 1
                      }
                      onClick={() =>
                        set({
                          ...content,
                          navigation: moveItemFn(
                            content.navigation,
                            index,
                            direction
                          ),
                        })
                      }
                    >
                      {direction === -1 ? "Move up" : "Move down"}
                    </button>
                  ))}
                  <button
                    onClick={() =>
                      set({
                        ...content,
                        navigation: content.navigation.filter(
                          (l) => l.id !== link.id
                        ),
                      })
                    }
                  >
                    Remove link
                  </button>
                </div>
              </div>
            ))}
            <button
              onClick={() =>
                set({
                  ...content,
                  navigation: [
                    ...content.navigation,
                    {
                      id: crypto.randomUUID(),
                      title: "New link",
                      href: "/",
                      visible: false,
                    },
                  ],
                })
              }
            >
              Add link
            </button>
          </section>
        )}
        {pageId === "home" && (
          <FeaturedMenu content={content} menu={editor.menu} onChange={set} />
        )}
      </fieldset>
      <section>
        <div className="cms-actions">
          <button onClick={() => setPreview(!preview)}>
            {preview ? "Close preview" : "Preview draft"}
          </button>
          <button onClick={() => setNarrow(!narrow)}>
            {narrow ? "Desktop width" : "Mobile width"}
          </button>
        </div>
        {preview && (
          <>
            <p>
              Preview interactions are disabled. Shared regions use their
              current published content unless you are editing that region.
            </p>
            <PreviewComponent narrow={narrow}>
              <PreviewContext.Provider
                value={{
                  preview: true,
                  footer:
                    pageId === "footer" ? content : editor.shared.footer || {},
                  navigation:
                    pageId === "navigation"
                      ? content.navigation
                      : editor.shared.navigation,
                }}
              >
                <SiteNoticeLayout
                  preview
                  settings={
                    pageId === "navigation"
                      ? announcementSettings(content)
                      : editor.shared.announcement
                  }
                >
                  {pageId === "menu" ? (
                    <MenuComponent menuPageData={draftPreview} />
                  ) : pageId === "home" ? (
                    <HomeComponent
                      homePageData={draftPreview}
                      error={null}
                      isLoading={false}
                    />
                  ) : pageId === "about" ? (
                    <AboutComponent aboutPageData={content} />
                  ) : pageId === "footer" ? (
                    <FooterComponent
                      imageUrl={content.sowLogo}
                      backgroundUrl={content.bkgImg}
                    />
                  ) : (
                    <NavComponent />
                  )}
                </SiteNoticeLayout>
              </PreviewContext.Provider>
            </PreviewComponent>
          </>
        )}
      </section>
    </>
  );
}
